import translate from "@insite/client-framework/Translate";
import Accordion from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Icon from "@insite/mobius/Icon";
import Trash from "@insite/mobius/Icons/Trash";
import Typography from "@insite/mobius/Typography";
import { CustomIncludeModalConfig } from "@thkStore/Components/CustomProducts/CustomRailDialog";
import { ClipboardItemState } from "@thkStore/Store/Widgets/Clipboard/ClipboardState";
import IncludesSelector from "@thkStore/Widgets/ClipboardSummary/IncludesSelector";
import React from "react";
import styled, { css } from "styled-components";

const HeaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    span {

        &[class^="TypographyStyle-sc-"] {
            font-size: 14px;
        }
    
        &[class^="IconWrapper-sc-"] {
            cursor: pointer;
        }
    }
`;

const StaticHeader = styled.div`
    background-color: #EBEBEB;
    padding: 8px;
    margin-bottom: 8px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.secondary.contrast};
`;

const PriceLine = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    margin-top: 4px;
    font-size: 13px;
`;

const AccordionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    
    [data-test-selector="sectionPanel"] {
        order: 2;
    }
`;

const SelectionsMessage = styled.div`
    background-color: ${({ theme }) => theme.colors.primary.main};
    color: ${({ theme }) => theme.colors.primary.contrast};
    order: 1;
    padding: 8px;
    margin: -8px 0 8px;

    @media (max-width:768px){
        display: none;
    }
`;

interface Props {
    item: ClipboardItemState;
    includesText: string | undefined;
    customIncludeModalConfig: CustomIncludeModalConfig;
    onRemoveItem: (productId: string) => void;
    onAddInclude: (parentProductId: string, includeProductId: string, productName: string) => void;
    onChangeIncludeQty: (parentProductId: string, includeProductId: string, productName: string, qty: number) => void;
    onRemoveInclude: (parentProductId: string, includeProductId: string) => void;
    onAddCustomInclude: (localId: string, parentProductId: string, includeProductId: string, productName: string, unitPrice: number, weight: number, length: number, g1: number, g2: number, daysToShip: string) => void;
    onChangeCustomInclude: (localId: string, parentProductId: string, includeProductId: string, productName: string, qty: number, unitPrice: number, weight: number, length: number, g1: number, g2: number, daysToShip: string) => void;
    onRemoveCustomInclude: (parentProductId: string, localId: string) => void;
}

const ClipboardAccordionItem: React.FC<Props> = ({ item, includesText, customIncludeModalConfig, onRemoveItem, onAddInclude, onChangeIncludeQty, onRemoveInclude, onAddCustomInclude, onChangeCustomInclude, onRemoveCustomInclude }) => {
    const handleRemoveClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        onRemoveItem(item.productId);
    };

    const titleContent = (
        <HeaderWrapper>
            <Typography>{item.productName} {item.viewOnly ? "" : `(${item.qtyOrdered})`}</Typography>
            {!item.viewOnly && <Icon src={Trash} size={16} onClick={handleRemoveClick}/>}
        </HeaderWrapper>
    );

    const headerProps = {
        css: css`
            margin-bottom: 8px;

            button {
                color: ${({ theme }) => theme.colors.text.main};
                background-color: #EBEBEB;
                flex-direction: row-reverse;
                justify-content: flex-end;
                gap: 5px;
                padding: 8px;
                border: none !important;
                border-bottom: 1px solid ${({ theme }) => theme.colors.secondary.contrast} !important;

                &:focus {
                    padding: 8px;
                    border: none !important;
                    border-bottom: 1px solid ${({ theme }) => theme.colors.secondary.contrast} !important;
                }

                [class^="HeaderWrapper-sc-"] {
                    gap: 5px;
                }
            }
        `,
    };

    const panelProps = {
        css: css`
            padding: 0;
            margin: -8px 0 8px;
            border: none;
            padding-left: 30px;

            [class^="SelectorWrapper-sc-"] {
                padding: 0;
            }
        `,
    };

    if (!item.addIncludes) {
        return (
            <StaticHeader>
                {titleContent}
                {item.unitPrice !== undefined && (
                    <PriceLine>
                        <Typography>{`$${item.unitPrice.toFixed(2)}`}</Typography>
                        {item.daysToShip && (
                            <Typography>{`Days to Ship: ${item.daysToShip}`}</Typography>
                        )}
                    </PriceLine>
                )}
            </StaticHeader>
        );
    }

    return (
        <AccordionWrapper>
            <ManagedAccordionSection title={titleContent} initialExpanded={true} headerProps={headerProps} panelProps={panelProps}>
                <Accordion headingLevel={3}>
                    <IncludesSelector
                        parentProductId={item.productId}
                        includesText={includesText}
                        selectedIncludes={item.includes}
                        selectedCustomIncludes={item.customIncludes}
                        customIncludeModalConfig={customIncludeModalConfig}
                        onAddInclude={(includeId, name) => onAddInclude(item.productId, includeId, name)}
                        onChangeIncludeQty={(includeId, name, qty) => onChangeIncludeQty(item.productId, includeId, name, qty)}
                        onRemoveInclude={(includeId) => onRemoveInclude(item.productId, includeId)}
                        onAddCustomInclude={(localId, includeId, name, unitPrice, weight, length, g1, g2, daysToShip) => onAddCustomInclude(localId, item.productId, includeId, name, unitPrice, weight, length, g1, g2, daysToShip)}
                        onChangeCustomInclude={(localId, includeId, name, qty, unitPrice, weight, length, g1, g2, daysToShip) => onChangeCustomInclude(localId, item.productId, includeId, name, qty, unitPrice, weight, length, g1, g2, daysToShip)}
                        onRemoveCustomInclude={(localId) => onRemoveCustomInclude(item.productId, localId)}
                    />
                </Accordion>
            </ManagedAccordionSection>
            {item.addIncludes && includesText && (
                <SelectionsMessage>
                    <Typography>{translate(`Make {0} Selections`, includesText)}</Typography>
                </SelectionsMessage>
            )}
        </AccordionWrapper>
    );
};

export default ClipboardAccordionItem;
